import { useMemo, useRef, useEffect } from 'react'
import type { CallbackDataParams } from 'echarts/types/dist/shared.js'
import Echarts from 'echarts-for-react'
import { toLower } from 'lodash-es'
import DragHandleIcon from '@mui/icons-material/DragHandle'

import { ISelectOption, LineSegmentGenericData } from '$/types'

import {
  makeRUoptions,
  formatWithCommasOrExponential,
  lrChartTooltipFormatter
} from 'common/utils/chart/chartUtils'
import Echart from 'common/components/Echart'
import { RiskUnitType } from 'features/RiskManagement/Components/LocalFilter'
import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import {
  THREE_DEC_ATTR_NAMES,
  ONE_DEC_ATTR_NAMES,
  ZERO_DEC_ATTR_NAMES,
  formatWithPrecision
} from './attributeFormatters'

import tailwind from '_/tailwind.config'

// #region typescript stuff
interface IDataInputGraph {
  showLoading: boolean
  riskUnit?: RiskUnitType
  XValues: number[]
  s1Values: LineSegmentGenericData[]
  s2Values: LineSegmentGenericData[]
  s1Label: string
  s2Label: string
  XLabel?: string
  YLabel?: string
  graphIndexNumber: number
  attributeName: string
  selectedAttributes: ISelectOption[]
  lineColor1: string
  lineColor2: string
  isContinuous: boolean
  isDraggable?: boolean
  yAxisOptions: LineSegmentGenericData[]
  dataView?: boolean
  getChartRef?: (ref: React.RefObject<Echarts | null>) => void
}

const LABEL_NAMES = {
  [RiskUnitType.TotalRisk]: 'Risk (RU/yr)',
  [RiskUnitType.RiskDensity]: 'Risk (RU/mi/yr)',
  distance: 'Distance (ft)'
} as const

type LabelKeys = keyof typeof LABEL_NAMES
type LabelValues = (typeof LABEL_NAMES)[LabelKeys]
type AxesLabelFunc = (y: RiskUnitType, x?: LabelValues) => AxesLabels

type AxesLabels = {
  y: LabelValues | ''
  x: LabelValues | ''
}

const axesLabelSelector: AxesLabelFunc = (
  y: RiskUnitType,
  x = LABEL_NAMES.distance
): AxesLabels => ({
  y: LABEL_NAMES[y],
  x
})

const AXES_LABELS_MAP: Record<string, AxesLabels | AxesLabelFunc> = {
  Excavation: axesLabelSelector,
  'Latent Damage': axesLabelSelector,
  'Stress Corrosion Cracking': axesLabelSelector,
  'Land Vehicle': axesLabelSelector,
  Flood: axesLabelSelector,
  Vandalism: axesLabelSelector,
  'Valve Equipment Failure': axesLabelSelector,
  'Earth Movement': axesLabelSelector,
  'External Valve Interference': axesLabelSelector,
  Flange: axesLabelSelector,
  'Girth Weld': axesLabelSelector,
  'Incorrect Operation': axesLabelSelector,
  'Incorrect Valve Operation': axesLabelSelector,
  'Construction Defect': axesLabelSelector,
  'External Corrosion': axesLabelSelector,
  'Seam Weld': axesLabelSelector,
  'Internal Corrosion': axesLabelSelector,
  'Manufacturing Defect': axesLabelSelector,
  Lightning: axesLabelSelector,
  Sleeve: axesLabelSelector,
  Coupling: axesLabelSelector,
  Bend: axesLabelSelector,
  Watercraft: axesLabelSelector,
  'Total Risk': axesLabelSelector
}

// !TEMP! delete this after backend fixes input/output field discrepancy
const FALLBACK_AXES_LABELS_MAP: Record<string, AxesLabels | AxesLabelFunc> = {
  LatentDamage: axesLabelSelector,
  StressCorrosionCracking: axesLabelSelector,
  LandVehicle: axesLabelSelector,
  ValveEquipmentFailure: axesLabelSelector,
  EarthMovement: axesLabelSelector,
  ExternalValveInterference: axesLabelSelector,
  GirthWeld: axesLabelSelector,
  IncorrectOperation: axesLabelSelector,
  IncorrectValveOperation: axesLabelSelector,
  ConstructionDefect: axesLabelSelector,
  ExternalCorrosion: axesLabelSelector,
  SeamWeld: axesLabelSelector,
  InternalCorrosion: axesLabelSelector,
  ManufacturingDefect: axesLabelSelector,
  TotalRisk: axesLabelSelector
}

type FormatterValueArg = number | string
type RUFormatter = (value: number) => string
type FallbackFormatter = (value: string) => string
// #endregion

const getAxesLabelsFromChartName = (
  chartName: string,
  riskUnitType?: RiskUnitType
): AxesLabels => {
  // !TEMP! remove the ?? after backend syncs field (chartName) values
  const _chartLabels =
    AXES_LABELS_MAP[chartName] ?? FALLBACK_AXES_LABELS_MAP[chartName]

  if (typeof _chartLabels === 'function' && riskUnitType) {
    // no second argument defaults to Distance (ft) for now
    return _chartLabels(riskUnitType)
  }

  return (_chartLabels as AxesLabels) ?? { y: '', x: '' }
}

// for DataManagement page to format various unit types
// RiskManagement does not pass these props
const getAxesLabelsFromProps = (YLabel?: string, XLabel?: string) => {
  const axesLabels: { x: string; y: string } = { x: '', y: '' }

  if (YLabel) {
    axesLabels.y = YLabel
  }

  if (XLabel) {
    axesLabels.x = XLabel
  }

  return axesLabels
}

const formatterOptions = makeRUoptions('ru')

// !TEMP! carry over. can probably stick with comma/expo formatter only
const getAxesValueFormatters = (
  isDate: boolean
): Record<string, RUFormatter | FallbackFormatter> => {
  return {
    [RiskUnitType.TotalRisk]: (value: number) =>
      formatWithCommasOrExponential(value, formatterOptions),
    [RiskUnitType.RiskDensity]: (value: number) =>
      formatWithCommasOrExponential(value, formatterOptions),
    [LABEL_NAMES.distance]: (value: number) =>
      formatWithCommasOrExponential(value, { isMeasure: true, isGraph: true }),
    Year: (value: FormatterValueArg) =>
      Number.isInteger(value) ? String(value) : '',
    fallback: (value: string) =>
      formatWithCommasOrExponential(value, {
        isDate: isDate,
        fallbackFormatter: (value) => {
          // don't display the null category label
          if (value === null || value === undefined || value === 'null') {
            return ''
          }
          
          const strVal = isDate
            ? String(value).split('-')[0]
            : String(value)

          // Do not use lodash's startCase function here, it removes special characters
          // Example: X-Ray becomes X Ray

          // NOTE: This is a temporary solution as this should be fixed on backend with replacing Code to Names
          const formattedStrValue = strVal.replace(
            /([a-zA-Z])(\d)|([\d])([a-zA-Z])|([a-z])([A-Z])/g,
            '$1$3$5 $2$4$6'
          )

          return formattedStrValue.length > 9
            ? formattedStrValue.trim().slice(0, 9).concat('...')
            : formattedStrValue
        }
      })
  }
}

const getAxisFormatters = (
  yUnits: FormatterValueArg,
  xUnits: FormatterValueArg,
  isDate: boolean,
  attributeName?: string
) => {
  const axesValueFormatters = getAxesValueFormatters(isDate)

  // need a way to detect year axes without unit types
  if (toLower(attributeName).includes('year')) {
    return [
      axesValueFormatters.Year,
      axesValueFormatters[xUnits] ?? axesValueFormatters.fallback
    ]
  }

  const trimmedName = attributeName?.trim() ?? ''

  let yAxisFormatter

  if (THREE_DEC_ATTR_NAMES.includes(trimmedName)) {
    yAxisFormatter = (v: string | number) => formatWithPrecision(v, 3, 'Finite')
  } else if (ONE_DEC_ATTR_NAMES.includes(trimmedName)) {
    yAxisFormatter = (v: string | number) => v
  } else if (ZERO_DEC_ATTR_NAMES.includes(trimmedName)) {
    yAxisFormatter = (v: string | number) => v
  } else {
    // keep existing fallback behaviour
    yAxisFormatter =
      typeof axesValueFormatters[yUnits] === 'function'
        ? (v: string | number) =>
            (axesValueFormatters[yUnits] as RUFormatter)(
              typeof v === 'string' ? parseFloat(v) : v
            )
        : axesValueFormatters.fallback
  }

  return [
    yAxisFormatter,
    axesValueFormatters[xUnits] ?? axesValueFormatters.fallback
  ]
}

interface ILineData {
  value: LineSegmentGenericData[]
  fromMeasure: number
  toMeasure: number
  visualMap: boolean
  fullDate?: string
}

const DataInputGraph = ({
  showLoading,
  riskUnit,
  XValues,
  s1Values,
  s2Values,
  XLabel,
  s1Label,
  s2Label,
  YLabel,
  graphIndexNumber,
  attributeName,
  selectedAttributes,
  lineColor1,
  lineColor2,
  isContinuous,
  isDraggable,
  yAxisOptions,
  dataView,
  getChartRef
}: IDataInputGraph) => {
  const isDate =
    attributeName.includes('Date') && !attributeName.includes('Data Source')
  // Note: Data is being populated on both ends of the range (From & To measures) to show the correct representation
  const { line1Data, line2Data } = useMemo(() => {
    const buildEntry = (
      x: number,
      val: LineSegmentGenericData,
      fromMeasure: number,
      toMeasure: number
    ): ILineData => {
      // use year only for date attributes, safely handle null/undefined
      let yearValue: string | null = null

      if (isDate) {
        const strVal = val == null ? null : String(val)
        yearValue = strVal?.includes('-') ? strVal.split('-')[0] : strVal
      }

      const value = isDate ? [x, yearValue] : [x, val]

      return {
        value,
        fromMeasure,
        toMeasure,
        visualMap: false,
        fullDate: isDate ? val?.toString() ?? '' : undefined
      }
    }

    const valuesToRanges = (
      accumulator: ILineData[],
      val: LineSegmentGenericData,
      index: number
    ) => {
      // Segment data from WIS and Data Views are different as WIS segments are continuous but Data Views has
      // overlaps/segments missing so we need to have two different ways of parsing the data
      if (isContinuous && XValues.length > 2) {
        // To keep the last range as same as the previous one instead of keeping same from and to measures for it
        if (
          XValues[XValues.length - 2] == XValues[XValues.length - 1] &&
          index == XValues.length - 2
        ) {
          accumulator.push(buildEntry(
            XValues[index],
            val,
            XValues[index - 1],
            XValues[index]
          ))
        } else {
          accumulator.push(buildEntry(
            XValues[index],
            val,
            XValues[index],
            XValues[index + 1] ?? XValues[index]
          ))
        }
      } else {
        accumulator.push(
          buildEntry(
            XValues[2 * index],
            val,
            XValues[2 * index],
            XValues[2 * index + 1] ?? XValues[2 * index]
          ),
          buildEntry(
            XValues[2 * index + 1],
            val,
            XValues[2 * index],
            XValues[2 * index + 1] ?? XValues[2 * index]
          )
        )
      }

      return accumulator
    }

    const line1Data = s1Values.reduce(valuesToRanges, [])
    const line2Data = s2Values.reduce(valuesToRanges, [])

    return {
      line1Data,
      line2Data
    }
  }, [XValues, s1Values, s2Values, isContinuous, isDate])

  const chartTitle = attributeName

  const axesLabels =
    getAxesLabelsFromProps(YLabel, XLabel) ||
    getAxesLabelsFromChartName(attributeName, riskUnit)

  const [yAxisFormatter, xAxisFormatter] = getAxisFormatters(
    axesLabels.y,
    axesLabels.x,
    isDate,
    attributeName
  )

  // ? what shold the title of the tooltip be
  const options = {
    color: line2Data.length ? [lineColor1, lineColor2] : [lineColor1],
    tooltip: {
      trigger: 'axis',
      confine: true,
      formatter: (params: CallbackDataParams[]) => {
        if (dataView) {
          params = params.reverse()
        }

        if (dataView && params.length >= 2) {
          const param1 = params[0].value as (string | number)[]
          const param2 = params[1].value as (string | number)[]

          if (param1[1] == param2[1] || param2[1] == null) {
            params.splice(1, 1)
          }
        }

        return lrChartTooltipFormatter(params, attributeName, dataView, undefined, isDate)
      }
    },
    grid: [
      {
        show: true,
        top: 8,
        bottom: 0,
        right: 32,
        left: 90,
        height: 108
      }
    ],
    axisPointer: {
      link: { xAxisIndex: 'all' }
    },
    xAxis: [
      {
        name: axesLabels.x,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold'
        },
        boundaryGap: false,
        type: 'category',
        max: 'dataMax',
        min: 'dataMin',
        axisTick: { show: false },
        axisLabel: {
          formatter: xAxisFormatter
        }
      }
    ],
    yAxis: [
      {
        scale: true,
        type:
          isDate || (yAxisOptions[0] && Number.isNaN(Number(yAxisOptions[0])))
            ? 'category'
            : 'value',
        name: axesLabels.y,
        nameRotate: 90,
        nameLocation: 'middle',
        nameGap: 76,
        axisLabel: {
          formatter: yAxisFormatter
        },
        nameTextStyle: {
          color: tailwind.theme.colors.black,
          fontWeight: 'bold'
        },
        axisLine: {
          show: true
        },
        axisTick: {
          show: false
        },
        data: yAxisOptions[0] !== null ? yAxisOptions : undefined
      }
    ],
    series: [
      {
        type: 'line',
        step: 'end',
        data: line1Data,
        name: s1Label,
        symbol: 'none',
        color: lineColor1
      },
      {
        type: 'line',
        step: 'end',
        data: line2Data,
        name: s2Label,
        symbol: 'none',
        color: lineColor2
      }
    ]
  }

  const chartRef = useRef(null)

  useEffect(() => {
    if (getChartRef) {
      getChartRef(chartRef)
    }
  }, [getChartRef])

  return (
    <div
      className={`mb-3 flex h-60 flex-col ${
        graphIndexNumber >= selectedAttributes.length ? 'opacity-20' : ''
      }`}
    >
      <div className='h-full w-full'>
        <GraphContainerCard
          title={chartTitle}
          optionsComponent={
            isDraggable && (
              <button className='drag-handle cursor-move text-gray-700'>
                <DragHandleIcon />
              </button>
            )
          }
        >
          <Echart option={options} showLoading={showLoading} ref={chartRef} />
        </GraphContainerCard>
      </div>
    </div>
  )
}

export default DataInputGraph
